import React, { useContext, useEffect, useState, ChangeEvent } from 'react';
import ProfileDetailsInput from '../../../components/Inputs/ProfileDetailsInput';
import Sidebar from '../BabysitterSidebar/BabysitterSidebar';
import './BabysitterEditProfile.css';
import { ProfileContext } from '../../../context/ProfileContext';
import EditIcon from '../../../assets/Edit 1.svg';
import StyledButton from '../../../components/Button/Button';
import Popup from '../../../components/Inputs/Popup';
import { useNavigate } from 'react-router-dom';
import { editProfile } from '../../../services/NetworkServices';
import { getEmail } from '../../../utils/auth-token-utils/cookie';
import ApiStatus from '../../../config/apiStatusConstants';
import CircularProgress from '@mui/material/CircularProgress';
import {
  AppBar,
  Box,
  Container,
  Grid,
  Toolbar,
  Typography,
} from '@mui/material';
import LeftArrow from '../../../assets/Left 1.svg';
import LogoutPopup from '../../../components/Inputs/LogoutPopup';
import { getStage } from '../../../services/NetworkServices';
import { storeStageNumber } from '../../../utils/auth-token-utils/cookie';
import TemporaryDrawer from '../../../components/TemporaryDrawer/TemporaryDrawer';

interface FormData {
  firstName: string;
  lastName: string;
  phone: string;
  profilePic: string;
  idFront: string;
  idBack: string;
}

const BabysitterEditProfile: React.FC = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('ProfileContext must be used within a ProfileProvider');
  }

  const {
    userProfile,
    handleProfilePicPreview,
    isProfilePicChanged,
    isGovtIdFrontChanged,
    isGovtIdBackChanged,
    updatedFrontPreview,
    updatedBackPreview,
    profilePicPreview,
    setTempProfilePic,
    tempProfilePic,
    updatedFrontFile,
    updatedBackFile,
    handleActiveTab,
    showLogoutPopup,
  } = context;

  handleActiveTab('profile');

  console.log('userProfile is ', userProfile);

  const [formData, setFormData] = useState<FormData>({
    firstName: userProfile.firstName || '',
    lastName: userProfile.lastName || '',
    phone: userProfile.phone || '',
    profilePic: userProfile.profilePic || '',
    idFront: userProfile.idFront || '',
    idBack: userProfile.idBack || '',
  });
  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [fieldChanged, setFieldChanged] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [initialValues, setInitialValues] = useState<FormData>({
    firstName: '',
    lastName: '',
    phone: '',
    profilePic: '',
    idFront: '',
    idBack: '',
  });

  const [isChangeSuccessful, setIsChangeSuccessful] = useState<boolean>(false);
  const navigate = useNavigate();
  const email = getEmail() || '';

  const handleContinue = () => {
    setIsChangeSuccessful(false);
  };

  // const formatPhoneNumber = (input: string) => {
  //   console.log("input is", input);
  //   console.log("input type is", typeof input);
  //   if (!input || typeof input !== "string") {
  //     return ""; // Return an empty string or handle it appropriately
  //   }
  //   // Remove all non-numeric characters

  //   // Format as XXX XXXX XXX
  //   const formattedNumber = input
  //     .toString()
  //     .replace(/(\d{3})(\d{0,4})(\d{0,3})/, "$1-$2-$3");

  //   // Trim the result to remove any trailing spaces
  //   return formattedNumber.trim();
  // };
  const formatPhoneNumber = (input: string) => {
    // Remove all non-numeric characters
    const cleaned = input.replace(/\D/g, '');

    // Insert hyphens dynamically based on the length of the string
    let formattedNumber = '';

    // First 3 digits (XXX)
    if (cleaned.length > 0) {
      formattedNumber += cleaned.substring(0, 3);
    }

    // Middle 4 digits (XXXX)
    if (cleaned.length > 3) {
      formattedNumber += '-' + cleaned.substring(3, 7);
    }

    // Last 3 digits (XXX)
    if (cleaned.length > 7) {
      formattedNumber += '-' + cleaned.substring(7, 10);
    }

    return formattedNumber;
  };

  const formatName = (input: string) => {
    const formattedName = input.replace(/[^a-zA-Z\s]/g, '');
    return formattedName;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, files } = event.target;
    setFieldChanged(true);

    if (type === 'file' && files && files.length > 0) {
      const file = files[0];
      setTempProfilePic(file);
      setFormData((prevData) => ({
        ...prevData,
        [name]: file,
      }));

      handleProfilePicPreview(URL.createObjectURL(file));
      navigate('/supervisor/change-profilepic');
    } else if (name === 'firstName' || name === 'lastName') {
      const trimmedValue = formatName(value).slice(0, 20);
      setFormData((prevData) => ({
        ...prevData,
        [name]: trimmedValue,
      }));
    } else if (name === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prevData) => ({
        ...prevData,
        [name]: formattedPhone,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleEdit = () => {
    navigate('/supervisor/update-businesscard');
  };

  const handleSaveChanges = async () => {
    try {
      setApiStatus(ApiStatus.LOADING);
      const updatedData = new FormData();

      // Append files if they have changed
      if (tempProfilePic) {
        updatedData.append('profilePic', tempProfilePic);
      }
      if (updatedFrontFile) {
        updatedData.append('idFront', updatedFrontFile);
      }
      if (updatedBackFile) {
        updatedData.append('idBack', updatedBackFile);
      }

      // Remove hyphens from phone number before appending
      const cleanedPhoneNumber = formData.phone.replace(/\D/g, ''); // Remove all non-numeric characters

      // Append all other form data fields
      for (const key in formData) {
        let value = formData[key as keyof FormData];

        if (key === 'phone') {
          // Use the cleaned phone number without hyphens
          value = cleanedPhoneNumber;
        }

        if (value !== initialValues[key as keyof FormData]) {
          updatedData.append(key, value);
        }
      }

      // Log form data for debugging
      for (let pair of (updatedData as any).entries()) {
        console.log(`${pair[0]}:`, pair[1]); // Logs key and value
      }

      // Send the API request
      const response = await editProfile(updatedData);
      const data = await response.data;
      if (data) {
        setApiStatus(ApiStatus.SUCCESS);
        setErrorMsg('');
        setIsChangeSuccessful(true);
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      setErrorMsg(error.response.data.error.message);
      console.error('Error updating profile:', error);
    }
  };

  useEffect(() => {
    const fetchStageDetails = async () => {
      try {
        console.log('inside use effect');
        const response = await getStage();
        const data = await response.data;
        if (data) {
          console.log('stage is', data.data.stage);
          const stage = data.data.stage;
          storeStageNumber(stage);
          if (stage === '1') {
            navigate('/supervisor/createProfile');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  useEffect(() => {
    setFormData({
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      phone: formatPhoneNumber(userProfile.phone?.toString() || ''), // Check if userProfile.phone exists
      profilePic: userProfile.profilePic,
      idFront: userProfile.idFront,
      idBack: userProfile.idBack,
    });
    setInitialValues({
      firstName: userProfile.firstName,
      lastName: userProfile.lastName,
      phone: formatPhoneNumber(userProfile.phone?.toString() || ''), // Check if userProfile.phone exists
      profilePic: userProfile.profilePic,
      idFront: userProfile.idFront,
      idBack: userProfile.idBack,
    });
  }, [userProfile]);

  const renderButton = () => {
    if (apiStatus === ApiStatus.LOADING) {
      return (
        <StyledButton>
          <CircularProgress />
        </StyledButton>
      );
    } else {
      return (
        <StyledButton
          onClick={handleSaveChanges}
          disabled={
            !(
              fieldChanged ||
              isGovtIdFrontChanged ||
              isGovtIdBackChanged ||
              isProfilePicChanged
            )
          }
        >
          Save Changes
        </StyledButton>
      );
    }
  };

  const renderErrorMsg = () => {
    console.log('error is', errorMsg);
    if (apiStatus === ApiStatus.FAILURE) {
      return <p style={{ color: 'white' }}>{errorMsg}</p>;
    }
    return null;
  };

  console.log('phone number in user profile is', typeof userProfile.phone);

  const handleBackClick = () => {
    navigate('/supervisor/dashboard');
  };

  return (
    <>
      {(isChangeSuccessful || showLogoutPopup) && (
        <div className="backdrop"> </div>
      )}
      <Box className={isChangeSuccessful ? 'popup-active' : ''}>
        <Box className="account-details">
          <Box
            sx={{
              display: { xs: 'block', sm: 'flex' },
              height: { xs: '100%', sm: '100vh' },
            }}
          >
            <AppBar
              position="static"
              sx={{
                display: { xs: 'block', sm: 'none' },
                backgroundColor: 'black',
              }}
            >
              <Toolbar sx={{ background: 'black' }}>
                <TemporaryDrawer />
              </Toolbar>
            </AppBar>
            <Box
              sx={{
                // backgroundColor: 'green',
                display: { xs: 'none', sm: 'flex' },
                width: { xs: '0%', sm: '35%', md: '28%' },
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  marginLeft: { sm: 2, md: 14 },
                  marginTop: { sm: 2, md: 4 },
                  marginBottom: { sm: 30, md: 4 },
                }}
              >
                <Sidebar />
              </Box>
            </Box>
            <Box
              sx={{
                // backgroundColor: 'yellow',
                width: { xs: '100%', sm: '65%', md: '72%' },
                flexGrow: 1,
                overflow: 'auto',
                height: '100%',
                paddingY: 4,
              }}
            >
              <Container>
                <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                  <img
                    src={LeftArrow}
                    onClick={handleBackClick}
                    className="arrow-sm"
                  />
                </Box>
                <Box className="edit-profile-pic">
                  <img
                    src={
                      isProfilePicChanged
                        ? profilePicPreview ||
                          'https://www.pngitem.com/pimgs/m/146-1468479_my-profile-icon-blank-profile-picture-circle-hd.png'
                        : formData.profilePic ||
                          'https://www.pngitem.com/pimgs/m/146-1468479_my-profile-icon-blank-profile-picture-circle-hd.png'
                    }
                    className="profilePic"
                  />
                  <Box className="edit-icon-container">
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="upload-profile-pic"
                      type="file"
                      name="profilePic"
                      onChange={handleChange}
                    />
                    <label htmlFor="upload-profile-pic">
                      <img src={EditIcon} />
                    </label>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    paddingTop: '20px',
                    width: { xs: '100%', sm: '100%', md: '60%' },
                  }}
                >
                  <ProfileDetailsInput
                    label="First Name"
                    value={formData.firstName}
                    name="firstName"
                    type="text"
                    onChange={handleChange}
                  />
                  <ProfileDetailsInput
                    label="Last Name"
                    value={formData.lastName}
                    name="lastName"
                    type="text"
                    onChange={handleChange}
                  />
                  <ProfileDetailsInput
                    label="Phone Number"
                    value={formData.phone}
                    name="phone"
                    type="text"
                    onChange={handleChange}
                  />

                  <ProfileDetailsInput
                    label="Email Address"
                    value={email}
                    name="email"
                    type="text"
                    isEmail
                  />
                </Box>
                <Box className="govtId">
                  <Typography>Business Card</Typography>
                  <StyledButton onClick={handleEdit}>Edit</StyledButton>
                </Box>
                <Box className="govtid-images" sx={{ mb: '60px' }}>
                  <img
                    src={
                      isGovtIdFrontChanged
                        ? updatedFrontPreview || ''
                        : formData.idFront || ''
                    }
                    // className="govtid-pic"
                    style={{ width: '100px' }}
                  />

                  <img
                    src={
                      isGovtIdBackChanged
                        ? updatedBackPreview || ''
                        : formData.idBack || ''
                    }
                    // className="govtid-pic"
                    style={{ width: '100px' }}
                  />
                </Box>
                <Box className="fixed-button-container">{renderButton()}</Box>
                {renderErrorMsg()}
              </Container>
            </Box>
          </Box>

          <Grid>
            {isChangeSuccessful && (
              <Popup
                title=<Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'opensans',
                    fontWeight: '700',
                    lineHeight: '1.4',
                  }}
                >
                  {' '}
                  Info Updated <br /> Successfully{' '}
                </Typography>
                message=<Typography variant="body2">
                  {' '}
                  Your details have been updated successfully. Thank you!
                </Typography>
                buttonText="Done"
                onButtonClick={handleContinue}
              />
            )}
            {showLogoutPopup && <LogoutPopup />}
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default BabysitterEditProfile;
